/**
 * 顾问设置 · 开单金额区间（仅超管，advisor:manage）。
 *
 * 销售给客户开顾问订单时，金额必须落在这个区间内。区间只影响之后新开的单，已开的单不变。
 *
 * 下限固定不低于 ¥699，不是随手定的：¥699 是自助版的公开价，顾问单比它还便宜，客户一比价就穿帮；
 * 且城市合伙人佣金按固定额发放，实付过低会让佣金倒挂。上限 ¥50000 只是防手滑（多打了几个零）。
 * 这两条由后端强制，这里只是把它们讲给超管听。
 */
import { useEffect } from 'react';
import { Alert, Button, Card, Descriptions, Form, InputNumber, Space, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdvisorOrderAmountRange, updateAdvisorOrderAmountRange } from '../../../api/advisor';
import { ORDER_AMOUNT_RANGE_KEY } from '../order-amount-range';

const { Text } = Typography;

type FormValues = { minYuan: number; maxYuan: number };

const yuan = (fen: number) => `¥${(fen / 100).toLocaleString('zh-CN')}`;
const fmt = (ms: number | null) => (ms == null ? '-' : new Date(ms).toLocaleString('zh-CN', { hour12: false }));

export default function AdvisorSettingsPage() {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const query = useQuery({ queryKey: ORDER_AMOUNT_RANGE_KEY, queryFn: getAdvisorOrderAmountRange, retry: false });
  const range = query.data;

  useEffect(() => {
    if (range) form.setFieldsValue({ minYuan: range.minFen / 100, maxYuan: range.maxFen / 100 });
  }, [range, form]);

  const saveMutation = useMutation({
    mutationFn: (v: FormValues) =>
      updateAdvisorOrderAmountRange({ minFen: Math.round(v.minYuan * 100), maxFen: Math.round(v.maxYuan * 100) }),
    onSuccess: (data) => {
      message.success(`已保存：${yuan(data.minFen)} – ${yuan(data.maxFen)}，之后新开的单按新区间校验`);
      queryClient.invalidateQueries({ queryKey: ORDER_AMOUNT_RANGE_KEY });
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? '保存失败');
    },
  });

  if (query.isError) {
    return <Alert type="error" showIcon message="读取设置失败" description="请刷新页面重试" />;
  }

  const floorYuan = range ? range.bounds.floorFen / 100 : 699;
  const ceilYuan = range ? range.bounds.ceilFen / 100 : 50000;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="顾问开单金额区间" loading={query.isLoading}>
        {range && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="当前生效">
                {yuan(range.minFen)} – {yuan(range.maxFen)}
                {range.usingDefaults && <Text type="secondary">（出厂默认，还没人调过）</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="出厂默认">
                {yuan(range.defaults.minFen)} – {yuan(range.defaults.maxFen)}
              </Descriptions.Item>
              <Descriptions.Item label="不可逾越的边界">
                {yuan(range.bounds.floorFen)} – {yuan(range.bounds.ceilFen)}
              </Descriptions.Item>
              {range.updatedAt != null && <Descriptions.Item label="上次修改">{fmt(range.updatedAt)}</Descriptions.Item>}
            </Descriptions>

            <Form
              name="order_amount_range"
              form={form}
              layout="inline"
              onFinish={(v) => saveMutation.mutate(v)}
              style={{ rowGap: 12 }}
            >
              <Form.Item
                name="minYuan"
                label="最低价（元）"
                rules={[
                  { required: true, message: '请填写最低价' },
                  { type: 'number', min: floorYuan, message: `不能低于 ¥${floorYuan}` },
                ]}
              >
                <InputNumber min={floorYuan} max={ceilYuan} precision={0} style={{ width: 160 }} prefix="¥" />
              </Form.Item>
              <Form.Item
                name="maxYuan"
                label="最高价（元）"
                dependencies={['minYuan']}
                rules={[
                  { required: true, message: '请填写最高价' },
                  { type: 'number', max: ceilYuan, message: `不能超过 ¥${ceilYuan}` },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const min = getFieldValue('minYuan');
                      return typeof value === 'number' && typeof min === 'number' && value < min
                        ? Promise.reject(new Error('最高价不能低于最低价'))
                        : Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber min={floorYuan} max={ceilYuan} precision={0} style={{ width: 160 }} prefix="¥" />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
                    保存
                  </Button>
                  <Button
                    onClick={() =>
                      form.setFieldsValue({ minYuan: range.defaults.minFen / 100, maxYuan: range.defaults.maxFen / 100 })
                    }
                  >
                    填入出厂默认
                  </Button>
                </Space>
              </Form.Item>
            </Form>

            <Alert
              type="info"
              showIcon
              message="只影响之后新开的订单，已经开出去的单不变。想让销售能开一口价，把最低价和最高价设成同一个数即可。"
              description={
                <>
                  最低价不能低于 {yuan(range.bounds.floorFen)}：这是自助版的公开价，顾问单比它便宜，客户一比价就穿帮；
                  合伙人佣金也按固定额发放，实付过低会倒挂。最高价上限 {yuan(range.bounds.ceilFen)} 只是防手滑。
                  每次修改都会写进审计日志。
                </>
              }
            />
          </Space>
        )}
      </Card>
    </Space>
  );
}
